package com.partejar.app.feature.entitlements.presentation

import com.partejar.app.feature.entitlements.data.CommercialState
import com.partejar.app.feature.entitlements.data.EntitlementRepository
import com.partejar.app.feature.entitlements.data.PersonalEntitlement
import com.partejar.app.feature.entitlements.data.WorkspaceEntitlement
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

/** Commercial data is advisory UX. It must never decide whether a session is valid. */
class EntitlementCoordinator(
    private val repository: EntitlementRepository,
    private val scope: CoroutineScope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
) {
    private val _personal = MutableStateFlow<CommercialState<PersonalEntitlement>>(CommercialState.Loading)
    val personal: StateFlow<CommercialState<PersonalEntitlement>> = _personal.asStateFlow()
    private val _workspace = MutableStateFlow<CommercialState<WorkspaceEntitlement>>(CommercialState.Loading)
    val workspace: StateFlow<CommercialState<WorkspaceEntitlement>> = _workspace.asStateFlow()

    fun onSessionReady() {
        refreshPersonal()
        refreshWorkspace()
    }

    fun onWorkspaceChanged() = refreshWorkspace()

    fun refreshPersonal() = scope.launch { reloadPersonal() }
    fun refreshWorkspace() = scope.launch { reloadWorkspace() }

    suspend fun reloadPersonal() {
        _personal.value = CommercialState.Loading
        _personal.value = runCatching { repository.personal() }
            .fold({ CommercialState.Available(it) }, { CommercialState.TemporarilyUnavailable(it) })
    }

    suspend fun reloadWorkspace() {
        _workspace.value = CommercialState.Loading
        _workspace.value = runCatching { repository.currentWorkspace() }
            .fold({ CommercialState.Available(it) }, { CommercialState.TemporarilyUnavailable(it) })
    }

    suspend fun activateFree(): Result<PersonalEntitlement> = runCatching {
        val entitlement = repository.activateFree()
        _personal.value = CommercialState.Available(entitlement)
        entitlement
    }.onFailure { _personal.value = CommercialState.TemporarilyUnavailable(it) }

    fun clear() {
        _personal.value = CommercialState.Loading
        _workspace.value = CommercialState.Loading
    }
}
